<template>
  <div id="template-side-panel" @click.self="closePanel">
    <div class="panel-wrapper" :class="{ 'close-animation': !closed }">
      <div class="panel d-flex flex-column custom-scrollbar-light">
        <div class="panel-top">
          <div class="title-wrapper">
            <div class="template-name">
              <h2>{{ template.name }}</h2>
              <button
                class="btn rename-btn"
                @click="onRename(template)"
                :tooltip="$t('template.tp-list-btn-rename')"
                tooltip-placement="right"
              >
                <span><i class="fa-light fa-pen"></i></span>
              </button>
            </div>
            <div class="template-edit-actions">
              <button
                class="btn btn-icon-only"
                @click="onEditSources(template)"
                :tooltip="$t('integration.title')"
                tooltip-placement="bottom"
              >
                <span><i class="fa-light fa-database"></i></span>
              </button>
              <button
                class="btn btn-icon-only"
                @click="onDuplicate(template)"
                :tooltip="$t('template.duplicate')"
                tooltip-placement="bottom"
              >
                <span><i class="fa-light fa-clone"></i></span>
              </button>
              <button
                class="btn btn-icon-only"
                @click="onDelete(template)"
                :tooltip="$t('template.tp-list-btn-delete')"
                tooltip-placement="bottom"
              >
                <span><i class="fa-light fa-trash"></i></span>
              </button>
              <button class="btn btn-icon-only close-panel" @click="closePanel">
                <i class="fa-light fa-times"></i>
              </button>
            </div>
          </div>
          <div class="template-info text-muted d-flex">
            <div>
              {{ $t("template.tp-panel-info-created") }}
              <br />
              {{ $t("template.tp-panel-info-updated") }}
              <br />
              {{ $t("template.tp-panel-info-integration") }}
              <br />
              Template Id
              <br />
              <br />
            </div>
            <div class="flex-grow-1">
              {{ new Date(template.created) | dateFormat("DD.MM.YYYY - HH:mm") }}
              <br />
              {{ new Date(template.lastEdit) | dateFormat("DD.MM.YYYY - HH:mm") }}
              <br />
              {{ template.contentType.name }}
              <br />
              {{ template.id }}
            </div>
          </div>

          <div class="actions">
            <div
              :tooltip="
                invalidSettings ? $t('template.settings-err') : loadingSettings ? $t('template.settings-loading') : ''
              "
              tooltip-placement="right"
            >
              <button
                class="btn btn-md btn-primary"
                :disabled="loadingSettings || (!loadingSettings && invalidSettings)"
                @click="
                  $router.push({
                    name: 'template',
                    params: { templateId: template.id, templateLanguage: template.language }
                  })
                "
              >
                {{ $t("template.tp-panel-btn-edit") }}
              </button>
            </div>
            <button
              class="btn btn-md btn-primary-outline"
              @click="onPublish"
              :disabled="$store.getters.versionIsTrial"
              :tooltip="$store.getters.versionIsTrial ? $t('trial.unavailable') : ''"
            >
              {{ $t("template.tp-panel-btn-publish") }}
            </button>
          </div>
        </div>
        <div class="panel-content">
          <Collapsible :isCollapsed="false" class="panel-collapsible">
            <StandardHeader
              :open="!collapsible.collapsed"
              class="publications-header"
              slot="toggle"
              slot-scope="collapsible"
            >
              <span slot="header" class="publications-title">
                {{ $t("template.tp-panel-title") }}
              </span>
            </StandardHeader>
            <div class="publications" slot="content">
              <p class="text-muted no-publications" v-if="publications.length == 0">
                {{ $t("template.tp-panel-unpublished") }}
              </p>
              <PublicationCard
                v-for="publication in publications"
                :key="publication.id"
                :publication="publication"
                :exportableSources="exportableSources"
                :triggeredSources="triggeredSources"
                :template="template"
                @start-export="showExportView"
                @start-push="startPush(publication)"
              />
            </div>
          </Collapsible>

          <Collapsible :isCollapsed="false" :disabled="loadingSettings" class="panel-collapsible">
            <StandardHeader
              :open="!collapsible.collapsed && !loadingSettings"
              class="publications-header"
              slot="toggle"
              slot-scope="collapsible"
            >
              <div slot="header" class="settings-title">
                <span>{{ $t("template.template-settings") }}</span>
                <span class="loading-spinner" v-show="loadingSettings">
                  <i class="fa-light fa-circle-notch fa-spin"></i>
                </span>
              </div>
            </StandardHeader>
            <div class="settings" slot="content">
              <DynamicForm
                v-if="formData.length"
                class="dynamic-form"
                :formData="formData"
                :source="templateSettingsCopy"
                @search-example-id="searchExampleId"
                @invalid-fields="validateFields"
                @form-mounted="invalidSettings = invalidForm"
              />

              <div class="settings-actions" v-if="formData.length > 0" :tooltip="invalidForm ? invalidFormMsg : ''">
                <SaveButton
                  class="btn-xs"
                  :buttonText="$t('save')"
                  :disabled="!changes || invalidForm"
                  :onSave="saveSettings"
                  :loading="saving"
                  :saved="saveComplete"
                  :saveErr="saveIncomplete"
                  @saveCompleteHandled="saveComplete = false"
                  @saveErrHandled="saveIncomplete = false"
                />
              </div>
            </div>
          </Collapsible>
        </div>
      </div>
    </div>
    <export-popover
      v-if="startExport"
      :publication="publicationForExport"
      @close="closeExportView"
      @successful-export="startExport = false"
    />
    <push-popover
      v-if="showPushView"
      :publication="publicationForPush"
      @close="showPushView = false"
      @successful-push="showPushView = false"
    />
  </div>
</template>

<script>
import orderBy from "lodash/orderBy";
import PublishTemplateModal from "@/components/modals/PublishTemplateModal";
import EditTemplateSources from "@/components/modals/EditTemplateSources";
import RenameModal from "@/components/modals/RenameModal";
import DuplicateTemplateModal from "@/components/modals/DuplicateTemplateModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import ExportPopover from "@/components/ExportPopover";
import PushPopover from "@/components/PushPopover";
import Collapsible from "@/components/Collapsible";
import StandardHeader from "@/components/StandardHeader";
import DynamicForm from "@/components/DynamicForm";
import PublicationCard from "@/components/PublicationCard";
import SaveButton from "@/components/SaveButton";
import ExampleIdSearch from "@/components/modals/ExampleIdSearch";

export default {
  props: {
    template: Object,
    publications: Array
  },
  data() {
    return {
      closed: true,
      listOpened: true,
      showPublicationStats: false,
      publishedSourceIds: new Set(),
      exportableSources: [],
      triggeredSources: [],
      startExport: false,
      publicationForExport: null,
      publicationForPush: null,
      formData: [],
      templateSettingsCopy: Object.assign({}, this.template.settings),
      loadingSettings: false,
      saving: false,
      saveComplete: false,
      saveIncomplete: false,
      invalidForm: false,
      invalidFormMsg: "",
      invalidSettings: false,
      showPushView: false
    };
  },
  methods: {
    closeExportView() {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t("warning"),
          description: this.$t("variable-mapping.close-description"),
          confirmButtonText: this.$t("close"),
          onConfirm: () => {
            this.publicationForExport = null;
            this.startExport = false;
          }
        },
        { width: 350, height: "auto" }
      );
    },
    showExportView(publication) {
      this.publicationForExport = publication;
      this.startExport = true;
    },
    onPublish() {
      this.$modal.show(
        PublishTemplateModal,
        {
          template: this.template,
          publications: orderBy(
            this.$store.state.templates.filter((t) => t.published),
            (t) => !!this.publications.find((p) => t.id == p.id),
            ["desc"]
          ),
          onSave: () => {
            this.$emit("success");
          }
        },
        { width: 350, height: "auto", minHeight: 400, adaptive: true }
      );
    },

    closePanel() {
      this.closed = !this.closed;
      this.$emit("toggled", this.closed);
      setTimeout(() => {
        this.$emit("close");
      }, 100);
    },
    onEditSources(template) {
      this.$store.dispatch("getContentSources").then((contentSources) => {
        this.$modal.show(
          EditTemplateSources,
          {
            contentSources,
            contentType: template.contentType,
            currentIntegrations: template.sources,
            onSave: (newSource) => {
              template.sources = newSource;
              this.$store.dispatch("updateTemplateMetadata", template);
              this.$emit("success");
            }
          },
          { width: 310, height: "auto" }
        );
      });
    },

    onRename(template) {
      this.$modal.show(
        RenameModal,
        {
          title: this.$t("rename-template-modal.title"),
          inputName: this.$t("rename-template-modal.new-name"),
          primaryButton: this.$t("rename-template-modal.save"),
          existingName: template.name,
          onSave: (newName) => {
            template.name = newName;
            this.$emit("success");
            this.$store.dispatch("updateTemplateMetadata", template);
          }
        },
        { width: 310, height: "auto" }
      );
    },

    onDelete(template) {
      if (this.publications.find((p) => p.publishedFromId == template.id)) {
        this.$modal.show(
          ConfirmModal,
          {
            title: this.$t("template.delete-error-title"),
            description: this.$t("template.delete-error-desc"),
            hideCancelButton: true
          },
          { width: 350, height: "auto" }
        );
      } else {
        this.$store.dispatch("deleteTemplate", template).then(() => {
          // this.showNotice();
          this.$emit("success");
          this.closePanel();
        });
      }
    },

    onDuplicate(template) {
      this.$modal.show(
        DuplicateTemplateModal,
        {
          title: this.$t("template.duplicate-title"),
          inputName: this.$t("template.cr-tp-name-label"),
          primaryButton: this.$t("template.cr-tp-btn-create"),
          inputPlaceholder: template.name,
          existingName: template.name,
          existingLang: template.language,
          onSave: (newInfo) => {
            newInfo.existingId = template.id;
            this.$store.dispatch("duplicateTemplate", newInfo);
            this.$emit("success");
          }
        },
        { width: 310, height: "auto" }
      );
    },
    checkExportAvailability() {
      if (this.publications.length) {
        this.$store.dispatch("getContentSources2").then(() => {
          this.exportableSources = this.$store.state.contentSources2
            .filter((cs) => cs.contentType.id === this.template.contentType.id && cs.dummy)
            .map((cs) => cs.id);
        });
      }
    },
    checkTriggerAvailability() {
      if (this.publications.length) {
        this.triggeredSources = [];
        this.publications.forEach((p) => {
          if (this.triggeredConnectorEnumNames.includes(p.sources[0].enumName)) {
            this.triggeredSources.push(p.sources[0].id);
          }
        });
      }
    },
    getTemplateSettings() {
      this.loadingSettings = true;
      this.$store
        .dispatch("getTemplateSettingsFields", this.template.id)
        .then((res) => {
          this.loadingSettings = false;
          this.formData = res;
        })
        .catch((e) => {
          this.loadingSettings = false;
        });
    },
    saveSettings() {
      this.saving = true;
      this.$store
        .dispatch("saveTemplateSettings", { id: this.template.id, settings: this.templateSettingsCopy })
        .then((res) => {
          this.saving = false;
          this.templateSettingsCopy = Object.assign({}, this.template.settings);
          this.saveComplete = true;
          this.invalidSettings = this.invalidForm;
        })
        .catch((e) => {
          this.saving = false;
          this.saveIncomplete = true;
          this.invalidSettings = this.invalidForm;
        });
    },
    searchExampleId() {
      this.$modal.show(
        ExampleIdSearch,
        {
          templateId: this.template.id,
          selectAsExampleId: (id) => {
            this.$set(this.templateSettingsCopy, "exampleId", id);
          }
        },
        { width: 310, height: "auto" }
      );
    },

    validateFields(data) {
      this.invalidForm = data.invalidForm;
      this.invalidFormMsg = data.invalidFormMsg;
    },
    startPush(publication) {
      this.publicationForPush = publication;
      this.showPushView = true;
    }
  },
  computed: {
    changes() {
      return JSON.stringify(this.template.settings) != JSON.stringify(this.templateSettingsCopy);
    },
    contentTypes() {
      return this.$store.state.contentSources;
    },
    triggeredConnectorEnumNames() {
      return this.$store.state.fixedConnectors.filter((c) => c.value != "XmlFeed").map((c) => c.value);
    }
  },
  mounted() {
    if (!this.template.settings) this.$set(this.template, "settings", {});
    this.checkExportAvailability();
    this.checkTriggerAvailability();
    this.getTemplateSettings();
  },
  watch: {
    publications: {
      handler: function() {
        this.checkExportAvailability();
        this.checkTriggerAvailability();
      }
    }
  },
  components: {
    ExportPopover,
    PushPopover,
    Collapsible,
    StandardHeader,
    DynamicForm,
    PublicationCard,
    SaveButton
  }
};
</script>

<style lang="less">
.close-animation {
  animation: slideOut 0.2s ease !important;
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(400px);
    opacity: 0.6;
  }
}

@keyframes slideIn {
  from {
    transform: translateX(300px);
    opacity: 0.6;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

#template-side-panel {
  position: fixed;
  z-index: 3;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  .panel-wrapper {
    animation: slideIn 0.4s ease;
    padding: 4px 2px 4px 0;
    border-radius: 10px;
    position: absolute;
    right: 0;
    top: 0;
    margin: 12px;
    height: calc(100% - 24px);
    width: clamp(450px, 50vw, 700px);
    max-width: calc(100% - 24px);
    background: #fff;
    box-shadow: 0 17px 9px 9px rgba(0, 0, 0, 0.1);
  }
  .panel {
    overflow: auto;
    height: 100%;
    .panel-top {
      padding: 24px;
      padding-bottom: 0;
    }
  }
  .panel-collapsible {
    padding: 12px 24px;
    border-top: 1px solid var(--alabaster);
  }
  .panel-collapsible:last-of-type {
    border-bottom: 1px solid var(--alabaster);
  }
  .template-edit-actions {
    display: flex;
    gap: 12px;
  }
  .title-wrapper {
    display: flex;
    justify-content: space-between;
  }
  .template-name {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    h2 {
      word-break: break-all;
      -webkit-hyphens: auto;
      -ms-hyphens: auto;
      hyphens: auto;
      margin-bottom: 0;
    }
    .rename-btn {
      font-size: 12px;
    }
  }

  .template-info {
    font-size: 12px;
    padding: 10px 0 10px;
    div:last-of-type {
      padding-left: 10px;
    }
  }

  .actions {
    display: flex;
    gap: 14px;
    padding-bottom: 24px;
  }
  .header-counter {
    color: var(--text-grey-primary);
  }
  .dynamic-form {
    // display: flex;
  }
  .settings-actions {
    margin-top: 12px;
  }
  .settings-title {
    position: relative;
    .loading-spinner {
      position: absolute;
      bottom: 1px;
      right: -20px;
    }
  }
}
</style>
